import { useMemo } from "react";
import { ArrowUpRight, ChevronRight, Images, MapPin, Play } from "lucide-react";
import Analytics from "../../../../../utils/Analytics";
import {
  useSliderState,
  groupTemplates,
} from "../../slider/SliderState";
import {
  getShowcaseItems,
  getImageSrc,
  getLowestRate,
  formatPrice,
} from "../../slider/templates/utils/showcase";
import localization from "../../../../../assets/localization";

interface TemplateAnchorProps {
  templates: any[];
  assistantId: string;
  threadId: string | null;
  isMobile: boolean;
  language: string;
}

// Bare host of an authored URL, or "" when it is not parseable — an operator
// can type anything into the link field, and a thrown URL() would take the
// whole message down with it.
const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

// Compact cards rendered inside the bot message that produced slider
// templates. They are the chat-side handle on that content: clicking one
// (re)opens the slider on it, including for cached messages after a reload.
export default function TemplateAnchor({
  templates,
  assistantId,
  threadId,
  isMobile,
  language,
}: TemplateAnchorProps) {
  const openFromAnchor = useSliderState((state) => state.openFromAnchor);
  const t = localization.get(language);

  const grouped = useMemo(() => groupTemplates(templates ?? []), [templates]);

  // KB-authored links are NOT slider content: they render as plain anchors
  // under the message, always, whenever the cited fact carries them —
  // deterministic, never filtered by the model or by slider grouping
  // (groupTemplates drops unknown types on purpose).
  const links = useMemo(
    () => (templates ?? []).filter((t: any) => t?.type === "link" && t?.url),
    [templates]
  );

  if (!grouped.length && !links.length) return null;

  const describe = (template: any) => {
    if (template.type === "videos") {
      const count = (template.templates ?? []).length;
      return {
        title: t.anchor_videos,
        subtitle: count > 1 ? `${count} ${t.videos}` : t.watch_video,
        thumbs: [],
        isMap: false,
        isVideo: true,
      };
    }

    if (template.type === "poi_map") {
      const points = template.points ?? [];
      return {
        title: template.title || t.anchor_places,
        subtitle: `${points.length} ${points.length === 1 ? t.cat_place.toLowerCase() : t.places}`,
        thumbs: [],
        isMap: true,
        isVideo: false,
      };
    }

    if (template.type === "itinerary") {
      const stops = template.stops ?? [];
      return {
        title: template.title || t.anchor_itinerary,
        subtitle: `${stops.length} ${stops.length === 1 ? t.stop : t.stops}`,
        thumbs: [],
        isMap: true,
        isVideo: false,
      };
    }

    if (template.type === "map") {
      const points = template.templates ?? [];
      const first = points[0];
      return {
        title: t.anchor_places,
        subtitle:
          points.length > 1
            ? `${points.length} ${t.places}`
            : first?.name || first?.address || t.map_directions,
        thumbs: [],
        isMap: true,
        isVideo: false,
      };
    }

    const items = getShowcaseItems(template);
    const thumbs = items
      .map((item) => getImageSrc(item.images?.[0] ?? {}))
      .filter(Boolean)
      .slice(0, 2);

    if (template.type === "quote") {
      const lowest = items
        .map((item) => getLowestRate(item))
        .filter((rate): rate is number => rate !== null);
      const priceLabel = lowest.length
        ? ` · ${t.from_price} ${formatPrice(Math.min(...lowest))}`
        : "";
      return {
        title: t.anchor_rooms,
        subtitle: `${items.length} ${
          items.length === 1 ? t.proposal : t.proposals
        }${priceLabel}`,
        thumbs,
        isMap: false,
        isVideo: false,
      };
    }

    // list_views
    if (items.length === 1) {
      return {
        title: items[0].title || t.anchor_discover,
        subtitle: t.view_photos,
        thumbs,
        isMap: false,
        isVideo: false,
      };
    }
    return {
      title: t.anchor_discover,
      subtitle: `${items.length} ${t.contents}`,
      thumbs,
      isMap: false,
      isVideo: false,
    };
  };

  return (
    <div className="msg-anchors">
      {grouped.map((template: any, index: number) => {
        const info = describe(template);
        return (
          <button
            key={index}
            className="msg-anchor"
            onClick={() =>
              openFromAnchor(threadId, assistantId, templates, isMobile, index)
            }
          >
            {info.isMap || !info.thumbs.length ? (
              <span className="msg-anchor-pin">
                {info.isMap ? (
                  <MapPin className="icon" />
                ) : info.isVideo ? (
                  <Play className="icon" />
                ) : (
                  <Images className="icon" />
                )}
              </span>
            ) : (
              <span
                className={`msg-anchor-stack ${
                  info.thumbs.length > 1 ? "double" : ""
                }`}
              >
                {info.thumbs.map((src, i) => (
                  <img key={i} src={src} alt="" className="msg-anchor-thumb" />
                ))}
              </span>
            )}
            <span className="msg-anchor-body">
              <span className="msg-anchor-title">{info.title}</span>
              <span className="msg-anchor-sub">{info.subtitle}</span>
            </span>
            <span className="msg-anchor-go">
              <ChevronRight className="icon" />
            </span>
          </button>
        );
      })}
      {links.map((link: any, index: number) => {
        // The host, not the whole URL: a truncated
        // "https://grandhotelliberty.it/offerte/liberty-box-experie…" is noise,
        // while the domain is the one thing a guest checks before tapping.
        const host = hostOf(link.url);
        return (
          <a
            key={`link-${index}`}
            className="msg-anchor msg-anchor-link"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              Analytics.track("link_click", {
                url: link.url,
                cta: "kb_link",
                source: "anchor",
              })
            }
          >
            <span className="msg-anchor-body">
              <span className="msg-anchor-title">
                {link.title || host || link.url}
              </span>
              {link.title && host && (
                <span className="msg-anchor-sub">{host}</span>
              )}
            </span>
            {/* Out, not forward: this one leaves the conversation. */}
            <span className="msg-anchor-go">
              <ArrowUpRight className="icon" />
            </span>
          </a>
        );
      })}
    </div>
  );
}
